/**
 * Goal schemas — Tier 1 targets / KPIs per user / firm.
 *
 * Example: "Bill 100 hrs/month", "Acquire 10 leads/month", "Complete
 * 50 VAT returns Q3". Dashboard widget shows progress.
 */

import { z } from 'zod';
import { idSchema, iso8601Schema, pageInput, currencySchema, booleanQuerySchema } from './common';
import { JURISDICTION_CODES } from '../reference/jurisdictions';

const jurisdictionSchema = z.enum(JURISDICTION_CODES as unknown as [string, ...string[]]);

export const goalMetricSchema = z.enum([
  'HOURS_BILLED',
  'LEADS_ACQUIRED',
  'ITEMS_COMPLETED',
  'ENGAGEMENT_STEPS_COMPLETED',
  'ENGAGEMENTS_WON',
  'ENGAGEMENTS_COMPLETED',
  'PROPOSALS_SENT',
  'PROPOSALS_ACCEPTED',
  'REVENUE',
  'CUSTOM',
]);
export type GoalMetric = z.infer<typeof goalMetricSchema>;

export const goalScopeTypeSchema = z.enum(['FIRM', 'JURISDICTION', 'ENGAGEMENT', 'COMPLIANCE_SERVICE', 'PROPOSAL']);
export type GoalScopeType = z.infer<typeof goalScopeTypeSchema>;

export const goalPeriodSchema = z.enum([
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'ANNUAL',
]);

/** Keep the form and API aligned on meaningful metric/scope combinations. */
export function goalMetricAllowed(metric: GoalMetric, scopeType: GoalScopeType): boolean {
  if (scopeType === 'ENGAGEMENT') return metric === 'ENGAGEMENTS_WON' || metric === 'ENGAGEMENTS_COMPLETED' || metric === 'REVENUE' || metric === 'CUSTOM';
  if (scopeType === 'PROPOSAL') return metric === 'PROPOSALS_SENT' || metric === 'PROPOSALS_ACCEPTED' || metric === 'CUSTOM';
  if (metric === 'ENGAGEMENTS_WON' || metric === 'ENGAGEMENTS_COMPLETED' || metric === 'PROPOSALS_SENT' || metric === 'PROPOSALS_ACCEPTED') return false;
  if (metric === 'LEADS_ACQUIRED') return scopeType === 'FIRM' || scopeType === 'JURISDICTION';
  if (metric === 'REVENUE') return scopeType !== 'COMPLIANCE_SERVICE';
  if (metric === 'ITEMS_COMPLETED') return true;
  if (metric === 'ENGAGEMENT_STEPS_COMPLETED') return scopeType !== 'COMPLIANCE_SERVICE';
  return true;
}

export function goalScopeIssue(input: {
  metric: GoalMetric;
  scopeType: GoalScopeType;
  jurisdiction?: string | null;
  engagementId?: string | null;
  serviceType?: string | null;
}): string | null {
  if (!goalMetricAllowed(input.metric, input.scopeType)) return `${input.metric} is not available for ${input.scopeType} goals`;
  if (input.scopeType === 'JURISDICTION' && !input.jurisdiction) return 'Jurisdiction is required';
  if (input.scopeType === 'COMPLIANCE_SERVICE' && (!input.jurisdiction || !input.serviceType)) {
    return 'Jurisdiction and compliance service are required';
  }
  return null;
}

const scopeFields = {
  scopeType: goalScopeTypeSchema.default('FIRM'),
  jurisdiction: jurisdictionSchema.nullable().optional(),
  engagementId: idSchema.nullable().optional(),
  serviceType: z.string().min(1).max(100).nullable().optional(),
};

export const goalOutputSchema = z.object({
  id: idSchema,
  ownerId: idSchema.nullable(),              // user whose goal it is (null when team-wide)
  firmWide: z.boolean().default(false),
  scopeType: goalScopeTypeSchema,
  jurisdiction: jurisdictionSchema.nullable(),
  engagementId: idSchema.nullable(),
  serviceType: z.string().max(100).nullable(),
  title: z.string().min(1).max(200),
  metric: goalMetricSchema,
  /** Set (and required) for REVENUE goals — rollup sums only that currency. */
  currency: currencySchema.nullable(),
  targetValue: z.number().positive(),
  period: goalPeriodSchema,
  startDate: iso8601Schema,
  endDate: iso8601Schema,
  currentValue: z.number().nonnegative().default(0),
  notes: z.string().max(2000).nullable(),
  createdAt: iso8601Schema,
  updatedAt: iso8601Schema,
});
export type GoalOutput = z.infer<typeof goalOutputSchema>;

export const createGoalInputSchema = z.object({
  ownerId: idSchema.optional(),
  firmWide: z.boolean().default(false),
  ...scopeFields,
  title: z.string().min(1).max(200),
  metric: goalMetricSchema,
  currency: currencySchema.nullable().optional(),
  targetValue: z.number().positive(),
  period: goalPeriodSchema,
  startDate: iso8601Schema,
  endDate: iso8601Schema,
  notes: z.string().max(2000).nullable().optional(),
}).superRefine((v, ctx) => {
  if (v.metric === 'REVENUE' && !v.currency) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['currency'], message: 'Currency is required for revenue goals' });
  }
  const issue = goalScopeIssue(v);
  if (issue) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['scopeType'], message: issue });
});
export type CreateGoalInput = z.infer<typeof createGoalInputSchema>;

export const updateGoalInputSchema = z.object({
  ownerId: idSchema.optional(),
  firmWide: z.boolean().optional(),
  scopeType: goalScopeTypeSchema.optional(),
  jurisdiction: jurisdictionSchema.nullable().optional(),
  engagementId: idSchema.nullable().optional(),
  serviceType: z.string().min(1).max(100).nullable().optional(),
  title: z.string().min(1).max(200).optional(),
  metric: goalMetricSchema.optional(),
  currency: currencySchema.nullable().optional(),
  targetValue: z.number().positive().optional(),
  period: goalPeriodSchema.optional(),
  startDate: iso8601Schema.optional(),
  endDate: iso8601Schema.optional(),
  currentValue: z.number().nonnegative().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type UpdateGoalInput = z.infer<typeof updateGoalInputSchema>;

export const listGoalsInputSchema = pageInput.extend({
  ownerId: idSchema.optional(),
  firmWide: booleanQuerySchema.optional(),
  metric: goalMetricSchema.optional(),
  period: goalPeriodSchema.optional(),
  scopeType: goalScopeTypeSchema.optional(),
  jurisdiction: jurisdictionSchema.optional(),
  engagementId: idSchema.optional(),
  serviceType: z.string().max(100).optional(),
});
export type ListGoalsInput = z.infer<typeof listGoalsInputSchema>;

export const goalProgressRowSchema = z.object({
  id: idSchema,
  label: z.string(),
  sub: z.string().nullable(),
  href: z.string(),
  date: iso8601Schema.nullable(),
  value: z.number(),
});
export type GoalProgressRow = z.infer<typeof goalProgressRowSchema>;